import * as React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { formValueSelector, reduxForm } from 'redux-form';
import type { InjectedFormProps } from 'redux-form';

import type { W_JobMaterialCost_BulkUpsert_RM } from 'ab-api/web/jobMaterialCost/bulkUpsert';

import type { RootState } from 'af-reducers';

import { JOB_MATERIAL_BULK_RATE_UPSERT } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';

import * as JobMaterialCostActions from 'af-actions/jobMaterialCost';

import RateUpdateFM from './formModel';
import { MaterialRateBulkUpdateForm } from '../../../../../Shared/Materials/MaterialRateBulkUpdateForm';

interface OwnProps {
	jobId: number;
	jobMaterialIds: number[];
	showModal: boolean;
	onSubmit: () => void;
	close: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<RateUpdateFM>;

const BulkRateUpdateModal: React.FC<Props> = (props) => {
	const {
		jobId,
		jobMaterialIds,
		showModal,
		shippingMarkup,
		salesMarkup,
		tax,
		timezone,
		updateShippingMarkup,
		updateSalesMarkup,
		updateTax,
		valid,
		close,
		change: _change,
		handleSubmit,
		upsert,
		onSubmit,
		initialize: _initialize,
	} = props;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const change = React.useMemo(() => _change, []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialize = React.useMemo(() => _initialize, []);

	React.useEffect(() => {
		if (showModal) {
			initialize(new RateUpdateFM());
		}
	}, [showModal, initialize]);

	const upsertMaterialCost = React.useCallback(async (form: RateUpdateFM) => {
		const rm: W_JobMaterialCost_BulkUpsert_RM = {
			jobMaterialIds,
			updateShippingMarkup: form.updateShippingMarkup,
			shippingMarkup: form.shippingMarkup,
			updateSalesMarkup: form.updateSalesMarkup,
			salesMarkup: form.salesMarkup,
			updateTax: form.updateTax,
			tax: form.tax,
		};
		await upsert(jobId, rm);
		onSubmit();
		close();
	}, [close, jobId, jobMaterialIds, onSubmit, upsert]);

	const formUpsert = React.useMemo(() => handleSubmit(upsertMaterialCost), [handleSubmit, upsertMaterialCost]);

	const handleFormSubmit = React.useCallback(() => {
		formUpsert();
	}, [formUpsert]);

	return (
		<CustomModal modalStyle="warning" showModal={showModal} size="xl">
			<CustomModal.Header
				closeModal={close}
				title="Edit Rates for Selected Materials?"
			/>
			<CustomModal.Body>
				<MaterialRateBulkUpdateForm
					change={change}
					levelWord="Job"
					salesMarkup={salesMarkup}
					shippingMarkup={shippingMarkup}
					tax={tax}
					timezone={timezone}
					updateSalesMarkup={updateSalesMarkup}
					updateShippingMarkup={updateShippingMarkup}
					updateTax={updateTax}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={close}
					style="secondary"
				/>
				<Button
					disabled={!valid}
					label="Update Rates"
					onClick={handleFormSubmit}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

const formSelect = formValueSelector(JOB_MATERIAL_BULK_RATE_UPSERT);

function mapStateToProps(state: RootState) {
	const { user: { companyData } } = state;

	if (!companyData) {
		throw new Error('Not logged in');
	}

	const { shippingMarkup, salesMarkup, tax, updateSalesMarkup, updateShippingMarkup, updateTax } = formSelect(
		state,
		RateUpdateFM.field('updateShippingMarkup'),
		RateUpdateFM.field('shippingMarkup'),
		RateUpdateFM.field('updateSalesMarkup'),
		RateUpdateFM.field('salesMarkup'),
		RateUpdateFM.field('updateTax'),
		RateUpdateFM.field('tax')
	) as RateUpdateFM;

	return {
		shippingMarkup,
		salesMarkup,
		tax,
		updateSalesMarkup,
		updateShippingMarkup,
		updateTax,
		timezone: companyData.timezone,
	};
}

function mapDispatchToProps() {
	return {
		upsert: JobMaterialCostActions.bulkUpsertJobMaterialCost,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const formConnector = reduxForm({
	form: JOB_MATERIAL_BULK_RATE_UPSERT,
	validate: RateUpdateFM.validate,
});

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector,
	formConnector
);

export default enhance(BulkRateUpdateModal);
